import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItemButton,
    ListItemText,
    InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createNotification } from '../../../utils/alerts';
import {
    PRIMARY,
    WARNING,
    ALLOWED_ATTACHMENT_EXTENSIONS,
    ALLOWED_ATTACHMENT_LABEL,
    ATTACHMENT_ACCEPT,
    getFileExtension,
    dedupeFiles,
    InstructionBubble,
} from './sharedUi';
import TimelineArticleCard from './TimelineArticleCard';
import Composer from './Composer';
import ContextPickerModal from './ContextPickerModal';

const defaultGetVersionKey = (version) => version?.version_id ?? version?.article_id ?? null;

/**
 * Núcleo compartilhado das telas de escrita/reescrita de matéria: chat
 * único (instruções + matérias geradas na mesma timeline rolável) com um
 * composer fixo no rodapé, estilo ChatGPT. Cada página consumidora fornece
 * o carregamento inicial de dados, o endpoint de stream e, opcionalmente,
 * um cabeçalho contextual, ações extras e fontes de contexto anexáveis
 * (`contextSources`, ex.: sugestões NeoFeed, notícias relacionadas).
 */
export default function ArticleWriter({
    backLabel,
    onBack,
    headerContent = null,
    extraTopActions = null,
    loading = false,
    resetToken,
    initialArticlesHistory = [],
    initialArticle = null,
    initialResponseId = null,
    getVersionKey = defaultGetVersionKey,
    streamFn,
    extraPayload,
    autoGenerateWhenEmpty = false,
    canGenerate,
    onValidationBlocked,
    generateBlockedTooltip,
    onGenerated,
    editorialAreas = null,
    editorialAreasLoading = false,
    contextSources = [],
}) {
    const isMobile = useMediaQuery('(max-width:900px)');

    /* ── Todas as versões — mais recente no índice 0 ── */
    const [articlesHistory, setArticlesHistory] = useState(initialArticlesHistory);

    /* ── Versões geradas nesta sessão (mais antiga no índice 0) ── */
    const [sessionVersions, setSessionVersions] = useState([]);

    /* ── Instruções desta sessão ── */
    const [sessionHistory, setSessionHistory] = useState([]);

    /* ── response_id em memória ── */
    const [responseId, setResponseId] = useState(initialResponseId);

    /* ── Input ── */
    const [instruction, setInstruction] = useState('');

    /* ── Anexos selecionados (ainda não enviados) ── */
    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef(null);

    /* ── Área editorial selecionada (opcional, define o tom de escrita) ── */
    const [selectedEditorialArea, setSelectedEditorialArea] = useState(null);
    const [editorialAreaModalOpen, setEditorialAreaModalOpen] = useState(false);
    const [editorialAreaSearch, setEditorialAreaSearch] = useState('');

    /* ── Modal "Adicionar contexto" ── */
    const [contextModalOpen, setContextModalOpen] = useState(false);
    const [contextModalTabKey, setContextModalTabKey] = useState(null);

    /* ── Stream ── */
    const [streaming, setStreaming] = useState(false);
    const [rawChunks, setRawChunks] = useState('');

    /* ── Contexto insuficiente para escrever a matéria (evento dedicado) ── */
    const [insufficientContext, setInsufficientContext] = useState(null);

    const abortRef        = useRef(null);
    const chatEndRef      = useRef(null);
    const textareaRef     = useRef(null);
    const autoGenerateRef = useRef(false);
    const initializedRef  = useRef(false);

    /* ── (Re)inicializar estado quando os dados iniciais ficam prontos ── */
    useEffect(() => {
        if (loading) {
            initializedRef.current = false;
            return;
        }
        if (initializedRef.current) return;
        initializedRef.current = true;

        setArticlesHistory(initialArticlesHistory || []);
        setSessionVersions([]);
        setSessionHistory([]);
        setInstruction('');
        setAttachments([]);
        setRawChunks('');
        setInsufficientContext(null);
        setSelectedEditorialArea(null);
        setResponseId(initialArticle?.response_id ?? initialResponseId ?? null);

        if (autoGenerateWhenEmpty && (!initialArticlesHistory || initialArticlesHistory.length === 0) && !initialArticle) {
            autoGenerateRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, resetToken]);

    /* ── Auto-scroll no chat ── */
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [sessionHistory, sessionVersions, streaming]);

    /* ── Seleção de anexos ── */
    const handleFilesSelected = useCallback((fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;

        const valid = [];
        let hasInvalid = false;

        incoming.forEach((file) => {
            const ext = getFileExtension(file.name);
            if (ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
                valid.push(file);
            } else {
                hasInvalid = true;
            }
        });

        if (hasInvalid) {
            createNotification(
                'error',
                `Alguns arquivos foram ignorados. Formatos aceitos: ${ALLOWED_ATTACHMENT_LABEL}.`,
                isMobile
            );
        }

        if (valid.length) {
            setAttachments((prev) => dedupeFiles(prev, valid));
        }

        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [isMobile]);

    const handleRemoveAttachment = useCallback((index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    }, []);

    /* ── Área editorial ── */
    const handleOpenEditorialAreaModal = useCallback(() => {
        setEditorialAreaSearch('');
        setEditorialAreaModalOpen(true);
    }, []);

    const handleSelectEditorialArea = useCallback((area) => {
        setSelectedEditorialArea(area);
        setEditorialAreaModalOpen(false);
    }, []);

    const handleRemoveEditorialArea = useCallback(() => {
        setSelectedEditorialArea(null);
    }, []);

    const filteredEditorialAreas = (editorialAreas || []).filter((area) => (
        area?.name?.toLowerCase().includes(editorialAreaSearch.trim().toLowerCase())
    ));

    /* ── Modal "Adicionar contexto" ── */
    const handleOpenContextModal = useCallback((key) => {
        setContextModalTabKey(key || null);
        setContextModalOpen(true);
    }, []);

    const handleClearContextGroup = useCallback((key) => {
        contextSources.find((source) => source.key === key)?.onClearAll?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextSources]);

    /* ── Gerar / Reescrever ── */
    const handleGenerate = useCallback(async () => {
        if (streaming) return;

        const currentInstruction = instruction.trim();
        const currentAttachments = attachments;

        if (canGenerate && !canGenerate({ instruction: currentInstruction, attachments: currentAttachments })) {
            onValidationBlocked?.();
            return;
        }

        const entryIndex = sessionHistory.length;

        setSessionHistory((prev) => [...prev, { instruction: currentInstruction, attachments: currentAttachments }]);
        setInstruction('');
        setAttachments([]);
        setStreaming(true);
        setRawChunks('');
        setInsufficientContext(null);

        const payload = { ...(extraPayload || {}) };
        if (currentInstruction) payload.instructions = currentInstruction;
        if (responseId) payload.previous_response_id = responseId;
        if (currentAttachments.length) payload.files = currentAttachments;
        if (selectedEditorialArea) payload.editorial_area_id = selectedEditorialArea.id;

        abortRef.current = new AbortController();

        try {
            await streamFn(
                payload,
                {
                    signal: abortRef.current.signal,
                    onEvent: ({ event, data }) => {
                        if (event === 'chunk') {
                            setRawChunks((prev) => prev + (data?.content ?? ''));
                        } else if (event === 'done') {
                            setResponseId(data?.response_id ?? null);
                            setRawChunks('');

                            // Registrar versão desta sessão
                            setSessionVersions((prev) => [...prev, data]);

                            // Confirmar anexos enviados com os dados retornados pela API (com URL de download)
                            if (data?.attachments?.length) {
                                setSessionHistory((prev) => prev.map((entry, i) => (
                                    i === entryIndex ? { ...entry, attachments: data.attachments } : entry
                                )));
                            }

                            // Atualizar lista global evitando duplicata
                            const key = data ? getVersionKey(data) : null;
                            if (key != null) {
                                setArticlesHistory((prev) => {
                                    const filtered = prev.filter((v) => getVersionKey(v) !== key);
                                    return [data, ...filtered];
                                });
                            }

                            onGenerated?.(data);
                        } else if (event === 'insufficient_context') {
                            // Não é uma versão válida: não entra no histórico (nem
                            // como versão, nem como instrução/anexo desta sessão).
                            setRawChunks('');
                            setInsufficientContext(data);
                            setSessionHistory((prev) => prev.filter((_, i) => i !== entryIndex));
                            setInstruction(currentInstruction);
                            setAttachments(currentAttachments);
                        }
                    },
                    onError: (detail) => {
                        createNotification('error', detail || 'Erro ao gerar matéria.', isMobile);
                        setStreaming(false);
                    },
                }
            );
        } catch (err) {
            if (err.name === 'AbortError') return;
            if (err.message !== 'Unauthorized') {
                createNotification('error', err.message || 'Erro ao gerar matéria. Tente novamente.', isMobile);
            }
        } finally {
            setStreaming(false);
        }
    }, [
        streaming, instruction, attachments, responseId, sessionHistory, extraPayload,
        streamFn, isMobile, canGenerate, onValidationBlocked, onGenerated, getVersionKey,
        selectedEditorialArea,
    ]);

    /* ── Geração automática quando não há histórico ── */
    useEffect(() => {
        if (!loading && autoGenerateRef.current) {
            autoGenerateRef.current = false;
            handleGenerate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    /* ── Cancelar stream em desmontagem ── */
    useEffect(() => {
        return () => abortRef.current?.abort();
    }, []);

    const trimmedInstruction = instruction.trim();
    const isGenerateBlocked = Boolean(canGenerate) && !canGenerate({ instruction: trimmedInstruction, attachments });
    const generateDisabled = streaming || isGenerateBlocked;

    /* ── Enter envia (Shift+Enter nova linha) ── */
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !generateDisabled) {
            e.preventDefault();
            handleGenerate();
        }
    };

    // Versões pré-sessão: as que estavam no banco ao abrir a página (do mais antigo para o mais novo)
    const preSessionItems = [...articlesHistory.slice(sessionVersions.length)].reverse();
    // Total de versões no chat (para o badge "Recente")
    const totalVersionsInChat = preSessionItems.length + sessionVersions.length;

    const isFirstEver = articlesHistory.length === 0 && sessionHistory.length === 0;
    const showEmptyState = isFirstEver && !streaming && !insufficientContext;

    const generateTooltip = isGenerateBlocked
        ? (generateBlockedTooltip || 'Adicione instruções ou um anexo válido para continuar')
        : streaming
            ? 'Aguarde a geração terminar'
            : '';

    /* ─────────────────────────────────────────────────────────── */
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: PRIMARY }} />
            </Box>
        );
    }

    return (
        <Box>

            {/* ── Topo ── */}
            <Box sx={{ mb: 1.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: isMobile ? 'stretch' : 'center',
                        justifyContent: 'space-between',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 0.8,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        sx={{ color: '#434851', textTransform: 'none', pl: 0, mb: 0.5, justifyContent: 'flex-start' }}
                    >
                        {backLabel}
                    </Button>
                    {extraTopActions && (
                        <Box sx={{ width: isMobile ? '100%' : 'auto', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                            {extraTopActions}
                        </Box>
                    )}
                </Box>

                {headerContent && <Box sx={{ mt: 1.2 }}>{headerContent}</Box>}
            </Box>

            {/* ── Timeline: instruções + matérias geradas ── */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}>
                    {/* Estado vazio */}
                    {showEmptyState && (
                        <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <AutoFixHighIcon sx={{ fontSize: 32, color: '#e0e0e0', mb: 0.8 }} />
                            <Typography sx={{ color: '#bbb', fontSize: '0.8rem' }}>
                                Nenhuma versão ainda.<br />
                                Clique em "Gerar matéria" para começar.
                            </Typography>
                        </Box>
                    )}

                    {/* Versões pré-sessão (histórico do banco) — do mais antigo para o mais novo */}
                    {preSessionItems.map((version, idx) => (
                        <React.Fragment key={getVersionKey(version) ?? `pre-${idx}`}>
                            <InstructionBubble text={version.instructions} attachments={version.attachments} />
                            <TimelineArticleCard
                                version={version}
                                getVersionKey={getVersionKey}
                                isLatest={idx === totalVersionsInChat - 1}
                            />
                        </React.Fragment>
                    ))}

                    {/* Interações desta sessão: bolha de instrução + card da matéria */}
                    {sessionHistory.map((entry, idx) => (
                        <React.Fragment key={`session-${idx}`}>
                            <InstructionBubble
                                text={entry.instruction}
                                attachments={sessionVersions[idx]?.attachments ?? entry.attachments}
                            />
                            {sessionVersions[idx] && (
                                <TimelineArticleCard
                                    version={sessionVersions[idx]}
                                    getVersionKey={getVersionKey}
                                    isLatest={preSessionItems.length + idx === totalVersionsInChat - 1}
                                />
                            )}
                        </React.Fragment>
                    ))}

                    {/* Resposta em andamento — conteúdo chegando via stream */}
                    {streaming && (
                        <Box sx={{
                            alignSelf: 'flex-start',
                            width: '100%',
                            border: '1px solid #eee',
                            borderRadius: '4px 14px 14px 14px',
                            backgroundColor: '#fafafa',
                            p: '16px 18px',
                        }}>
                            {rawChunks ? (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <CircularProgress size={13} sx={{ color: PRIMARY }} />
                                        <Typography sx={{ fontSize: '0.78rem', color: '#888' }}>
                                            Recebendo conteúdo…
                                        </Typography>
                                    </Box>
                                    <Typography component="pre" sx={{
                                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                        fontFamily: 'inherit', fontSize: '0.9rem',
                                        color: '#333', lineHeight: 1.8, m: 0,
                                    }}>
                                        {rawChunks}
                                    </Typography>
                                </>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                    <CircularProgress size={16} sx={{ color: PRIMARY }} />
                                    <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>
                                        Iniciando geração…
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Contexto insuficiente para gerar a matéria */}
                    {!streaming && insufficientContext && (
                        <Box sx={{
                            alignSelf: 'flex-start',
                            width: '100%',
                            border: `1px solid ${WARNING}40`,
                            borderRadius: '4px 14px 14px 14px',
                            backgroundColor: `${WARNING}08`,
                            p: '18px 20px',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            textAlign: 'center', gap: 1.2,
                        }}>
                            <WarningAmberIcon sx={{ fontSize: 36, color: WARNING }} />
                            <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '0.92rem', maxWidth: 460 }}>
                                {insufficientContext.message || 'Contexto insuficiente para escrever a matéria.'}
                            </Typography>
                            {insufficientContext.reason && (
                                <Typography sx={{ fontSize: '0.82rem', color: '#777', maxWidth: 460 }}>
                                    {insufficientContext.reason}
                                </Typography>
                            )}
                            {insufficientContext.missing_information?.length > 0 && (
                                <Box sx={{
                                    mt: 0.5, textAlign: 'left', maxWidth: 460, width: '100%',
                                    backgroundColor: `${WARNING}0c`, border: `1px solid ${WARNING}30`,
                                    borderRadius: '10px', p: '12px 16px',
                                }}>
                                    <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: WARNING, mb: 0.8 }}>
                                        Informações faltantes
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2.2, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                        {insufficientContext.missing_information.map((info, idx) => (
                                            <Typography key={idx} component="li" sx={{ fontSize: '0.8rem', color: '#555' }}>
                                                {info}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            <Typography sx={{ fontSize: '0.76rem', color: '#aaa' }}>
                                Adicione mais instruções ou anexos e tente novamente.
                            </Typography>
                        </Box>
                    )}

                <div ref={chatEndRef} />
            </Box>

            {/* ── Composer flutuante ── */}
            <Box sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                width: '100%',
                pt: '1vw',
            }}>
                <Composer
                    instruction={instruction}
                    onInstructionChange={setInstruction}
                    onKeyDown={handleKeyDown}
                    disabled={streaming}
                    placeholder={isFirstEver ? 'Instrução opcional antes de gerar…' : 'Ex: "Deixe o texto mais analítico"'}
                    textareaRef={textareaRef}
                    attachments={attachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenFilePicker={() => fileInputRef.current?.click()}
                    editorialAreas={editorialAreas}
                    selectedEditorialArea={selectedEditorialArea}
                    onOpenEditorialAreaModal={handleOpenEditorialAreaModal}
                    onRemoveEditorialArea={handleRemoveEditorialArea}
                    contextSources={contextSources}
                    onOpenContextModal={handleOpenContextModal}
                    onClearContextGroup={handleClearContextGroup}
                    onSend={handleGenerate}
                    sendDisabled={generateDisabled}
                    sendLabel={streaming ? 'Gerando…' : isFirstEver ? 'Gerar matéria' : 'Reescrever'}
                    sendIconOnly={!isFirstEver}
                    sendIcon={
                        streaming
                            ? <CircularProgress size={15} sx={{ color: '#fff' }} />
                            : isFirstEver
                                ? <AutoFixHighIcon />
                                : <SendIcon />
                    }
                    sendTooltip={generateTooltip}
                    isMobile={isMobile}
                />

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFilesSelected(e.target.files)}
                />
            </Box>

            {/* ════════ MODAL — Seleção de área editorial ════════ */}
            <Dialog
                open={editorialAreaModalOpen}
                onClose={() => setEditorialAreaModalOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Área Editorial</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Box sx={{ p: 2, pb: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            autoFocus
                            placeholder="Buscar área editorial…"
                            value={editorialAreaSearch}
                            onChange={(e) => setEditorialAreaSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: PRIMARY } }}
                        />
                    </Box>

                    {editorialAreasLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} sx={{ color: PRIMARY }} />
                        </Box>
                    ) : filteredEditorialAreas.length === 0 ? (
                        <Typography sx={{ fontSize: '0.85rem', color: '#999', textAlign: 'center', p: 3 }}>
                            Nenhuma área editorial encontrada.
                        </Typography>
                    ) : (
                        <List sx={{ maxHeight: 320, overflowY: 'auto', py: 0 }}>
                            {filteredEditorialAreas.map((area) => (
                                <ListItemButton
                                    key={area.id}
                                    selected={selectedEditorialArea?.id === area.id}
                                    onClick={() => handleSelectEditorialArea(area)}
                                    sx={{
                                        '&.Mui-selected': { backgroundColor: `${PRIMARY}12` },
                                        '&.Mui-selected:hover': { backgroundColor: `${PRIMARY}1c` },
                                    }}
                                >
                                    <ListItemText
                                        primary={area.name}
                                        primaryTypographyProps={{ fontSize: '0.88rem' }}
                                    />
                                    {selectedEditorialArea?.id === area.id && (
                                        <CheckCircleOutlineIcon sx={{ fontSize: 18, color: PRIMARY }} />
                                    )}
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    {selectedEditorialArea && (
                        <Button
                            onClick={() => { handleRemoveEditorialArea(); setEditorialAreaModalOpen(false); }}
                            sx={{ textTransform: 'none', color: '#d32f2f', mr: 'auto' }}
                        >
                            Remover seleção
                        </Button>
                    )}
                    <Button onClick={() => setEditorialAreaModalOpen(false)} sx={{ textTransform: 'none', color: '#666' }}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ════════ MODAL — Adicionar contexto ════════ */}
            <ContextPickerModal
                open={contextModalOpen}
                onClose={() => setContextModalOpen(false)}
                sources={contextSources}
                initialTabKey={contextModalTabKey}
            />
        </Box>
    );
}
